// Falling-block render (M10.1): a pooled cube per falling entity, tinted by the
// block's map colour. Client-only (ENG-1); the sim owns the physics + landing.
import * as THREE from 'three';
import type { FallingBlock } from '../../sim/entities/fallingblock';
import type { Registries } from '../../sim/registry/load';

export class FallingBlockRenderer {
  private readonly group = new THREE.Group();
  private readonly meshes = new Map<number, THREE.Mesh>();
  private readonly geo = new THREE.BoxGeometry(1, 1, 1);
  private readonly colorOf: (state: number) => number;

  constructor(scene: THREE.Scene, registries: Registries) {
    scene.add(this.group);
    this.colorOf = (state) => {
      const def = registries.blocks.byName.get(registries.states.blockName[state] ?? '');
      const hex = def?.mapColor ?? '#b0a080';
      return parseInt(hex.slice(1), 16);
    };
  }

  update(blocks: readonly FallingBlock[]): void {
    const seen = new Set<number>();
    for (const b of blocks) {
      seen.add(b.id);
      let m = this.meshes.get(b.id);
      if (!m) {
        m = new THREE.Mesh(this.geo, new THREE.MeshBasicMaterial({ color: this.colorOf(b.state) }));
        this.meshes.set(b.id, m);
        this.group.add(m);
      }
      // The sim stores the block bottom at `y`; the box is centred, so + 0.5.
      m.position.set(b.x, b.y + 0.5, b.z);
    }
    for (const [id, m] of this.meshes) {
      if (seen.has(id)) continue;
      this.group.remove(m);
      (m.material as THREE.Material).dispose();
      this.meshes.delete(id);
    }
  }

  clear(): void {
    for (const m of this.meshes.values()) {
      this.group.remove(m);
      (m.material as THREE.Material).dispose();
    }
    this.meshes.clear();
  }
}
